import { useMemo } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import type { CrawlStatus } from '../../../types/knowledge';
import { formatCrawlStatus as formatCrawlStatusValue } from '../crawlFormatters';
import { filterCrawlsByStartUrlSearch } from '../filterCrawlsByStartUrlSearch';
import { useCrawlListSearchParams } from './useCrawlListSearchParams';
import { useCrawlLists } from './useCrawlLists';
import { useCrawlSchedules } from './useCrawlSchedules';
import { useCrawlSelection } from './useCrawlSelection';
import { useCrawlStartForm } from './useCrawlStartForm';

type UseKnowledgeCrawlsTabOptions = {
  databaseId: string;
  canManageCrawls: boolean;
};

export function useKnowledgeCrawlsTab({
  databaseId,
  canManageCrawls,
}: UseKnowledgeCrawlsTabOptions) {
  const { translate, locale } = useI18n();
  const lists = useCrawlLists({ databaseId });
  const startForm = useCrawlStartForm({ databaseId });
  const selection = useCrawlSelection({
    databaseId,
    crawlScheduleByJobId: lists.crawlScheduleByJobId,
    onCrawlContinued: lists.handleCrawlContinuedAfterSelection,
  });
  const schedules = useCrawlSchedules({
    databaseId,
    crawlScheduleByJobId: lists.crawlScheduleByJobId,
  });
  const crawlSearch = useCrawlListSearchParams();

  const formatCrawlStatus = (status: CrawlStatus) =>
    formatCrawlStatusValue(status, translate);

  const filteredCrawlsMapping = useMemo(
    () =>
      filterCrawlsByStartUrlSearch(
        lists.crawlsMapping,
        crawlSearch.normalizedCrawlSearch,
      ),
    [lists.crawlsMapping, crawlSearch.normalizedCrawlSearch],
  );
  const filteredCrawlsNeedingAttention = useMemo(
    () =>
      filterCrawlsByStartUrlSearch(
        lists.crawlsNeedingAttention,
        crawlSearch.normalizedCrawlSearch,
      ),
    [lists.crawlsNeedingAttention, crawlSearch.normalizedCrawlSearch],
  );
  const filteredCrawlsInProgress = useMemo(
    () =>
      filterCrawlsByStartUrlSearch(
        lists.crawlsInProgress,
        crawlSearch.normalizedCrawlSearch,
      ),
    [lists.crawlsInProgress, crawlSearch.normalizedCrawlSearch],
  );
  const filteredCrawlsCompleted = useMemo(
    () =>
      filterCrawlsByStartUrlSearch(
        lists.crawlsCompleted,
        crawlSearch.normalizedCrawlSearch,
      ),
    [lists.crawlsCompleted, crawlSearch.normalizedCrawlSearch],
  );
  const filteredCrawlsFailed = useMemo(
    () =>
      filterCrawlsByStartUrlSearch(
        lists.crawlsFailed,
        crawlSearch.normalizedCrawlSearch,
      ),
    [lists.crawlsFailed, crawlSearch.normalizedCrawlSearch],
  );

  const visibleFilteredCrawlCount =
    filteredCrawlsMapping.length +
    filteredCrawlsNeedingAttention.length +
    filteredCrawlsInProgress.length +
    filteredCrawlsCompleted.length +
    filteredCrawlsFailed.length;

  return {
    translate,
    canManageCrawls,
    crawls: lists.crawls,
    liveStatusByCrawlId: lists.liveStatusByCrawlId,
    filteredCrawlsMapping,
    filteredCrawlsNeedingAttention,
    filteredCrawlsInProgress,
    filteredCrawlsCompleted,
    filteredCrawlsFailed,
    visibleFilteredCrawlCount,
    crawlSearchInput: crawlSearch.crawlSearchInput,
    handleCrawlSearchChange: crawlSearch.handleCrawlSearchChange,
    hasActiveCrawlSearch: crawlSearch.hasActiveCrawlSearch,
    crawlScheduleByJobId: lists.crawlScheduleByJobId,
    directoryUsersById: lists.directoryUsersById,
    crawlUrl: startForm.crawlUrl,
    crawlMaxDepth: startForm.crawlMaxDepth,
    crawlMaxPages: startForm.crawlMaxPages,
    crawlUseStartUrlAsPrefixFilter: startForm.crawlUseStartUrlAsPrefixFilter,
    crawlAllowSubdomains: startForm.crawlAllowSubdomains,
    crawlExcludedUrls: startForm.crawlExcludedUrls,
    setCrawlUrl: startForm.setCrawlUrl,
    setCrawlMaxDepth: startForm.setCrawlMaxDepth,
    setCrawlMaxPages: startForm.setCrawlMaxPages,
    onCrawlMaxDepthBlur: startForm.onCrawlMaxDepthBlur,
    onCrawlMaxPagesBlur: startForm.onCrawlMaxPagesBlur,
    setCrawlUseStartUrlAsPrefixFilter:
      startForm.setCrawlUseStartUrlAsPrefixFilter,
    setCrawlAllowSubdomains: startForm.setCrawlAllowSubdomains,
    setCrawlExcludedUrls: startForm.setCrawlExcludedUrls,
    resetCrawlForm: startForm.resetCrawlForm,
    handleStartCrawl: startForm.handleStartCrawl,
    preventInvalidNumericKey: startForm.preventInvalidNumericKey,
    isStartingCrawl: startForm.isStartingCrawl,
    handleManualRefresh: lists.handleManualRefresh,
    isMappingExpanded: lists.isMappingExpanded,
    isNeedsAttentionExpanded: lists.isNeedsAttentionExpanded,
    isInProgressExpanded: lists.isInProgressExpanded,
    isCompletedExpanded: lists.isCompletedExpanded,
    isFailedExpanded: lists.isFailedExpanded,
    toggleMappingExpanded: lists.toggleMappingExpanded,
    toggleNeedsAttentionExpanded: lists.toggleNeedsAttentionExpanded,
    toggleInProgressExpanded: lists.toggleInProgressExpanded,
    toggleCompletedExpanded: lists.toggleCompletedExpanded,
    toggleFailedExpanded: lists.toggleFailedExpanded,
    expandAllCrawlGroups: lists.expandAllCrawlGroups,
    collapseAllCrawlGroups: lists.collapseAllCrawlGroups,
    selectionCrawl: selection.selectionCrawl,
    selectionNodes: selection.selectionNodes,
    selectedNodeIds: selection.selectedNodeIds,
    setSelectedNodeIds: selection.setSelectedNodeIds,
    isSelectionLoading: selection.isSelectionLoading,
    isSelectionSubmitting: selection.isSelectionSubmitting,
    isAwaitingSelectionStart: selection.isAwaitingSelectionStart,
    isScheduledAwaitingSelection: selection.isScheduledAwaitingSelection,
    openSelection: selection.openSelection,
    handleCloseSelection: selection.handleCloseSelection,
    confirmSelection: selection.confirmSelection,
    handleRestartCrawl: lists.handleRestartCrawl,
    handleRetryFailedCrawl: lists.handleRetryFailedCrawl,
    handleResumeAbandonedCrawl: lists.handleResumeAbandonedCrawl,
    handleRequestDeleteCrawl: lists.handleRequestDeleteCrawl,
    openScheduleModal: schedules.openScheduleModal,
    scheduleModalCrawl: schedules.scheduleModalCrawl,
    scheduleModalExisting: schedules.scheduleModalExisting,
    handleSaveSchedule: schedules.handleSaveSchedule,
    handleCloseScheduleModal: schedules.handleCloseScheduleModal,
    isScheduleSubmitting: schedules.isScheduleSubmitting,
    pendingScheduleDelete: schedules.pendingScheduleDelete,
    setPendingScheduleDelete: schedules.setPendingScheduleDelete,
    handleDeleteSchedule: schedules.handleDeleteSchedule,
    pendingDelete: lists.pendingDelete,
    setPendingDelete: lists.setPendingDelete,
    confirmDeleteCrawl: lists.confirmDeleteCrawl,
    isDeletingCrawl: lists.isDeletingCrawl,
    formatCrawlStatus,
    locale,
  };
}
